import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useKeycloak } from "@react-keycloak/web";
import { connect } from "react-redux";
import { setLogIn } from "redux/reducers/keycloak/actions";
import { KeycloakAuthTypes } from "./types";
import { Dispatch } from "redux";
import { updateLanguage } from "redux/reducers/global/actions";
import { getUserPreferences } from "services/requests/v1/user";
import { setUserPreferences } from "redux/reducers/preferences/actions";
import { getConfigurationParams } from "services/requests/v1/configuration";
import { ConfigurationParamsType } from "types/generic";
import { getEnvVariable, REACT_APP_TITLE_NAME } from "utils/environment";

const KeycloakAuth = ({
    onSetLogin,
    updateLanguage,
    updatePreferences,
}: KeycloakAuthTypes) => {
    const history = useHistory();
    const { keycloak }: any = useKeycloak();

    const saveTokenOnLocalStorage = useCallback((token: string | undefined) => {
        if (!token) return;
        localStorage.setItem("token", token);
    }, []);

    const [configurationParamsSaved, setConfigurationParamsSaved] =
        useState<boolean>(false);

    const isTokenExpired = (token: any) => {
        const base64Url = token.split(".")[1];
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split("")
                .map(function (c) {
                    return (
                        "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2)
                    );
                })
                .join("")
        );

        const { exp } = JSON.parse(jsonPayload);
        const expired = Date.now() >= exp * 1000;
        return expired;
    };

    const onHandleUpdateToken = useCallback(() => {
        const ONE_MINUTE = 60000;
        setInterval(() => {
            keycloak
                .updateToken(70)
                .then((refreshed: boolean) => {
                    if (refreshed) {
                        console.log("Token was successfully refreshed");
                        saveTokenOnLocalStorage(keycloak?.token);
                    } else {
                        if (
                            keycloak.authenticated &&
                            !isTokenExpired(keycloak?.token)
                        ) {
                            console.log("Token is still valid");
                        } else {
                            keycloak.logout();
                        }
                    }
                })
                .catch(() => {
                    //console.log("Session has expired");
                    keycloak.logout();
                });
        }, ONE_MINUTE);
    }, [keycloak, saveTokenOnLocalStorage]);

    useEffect(() => {
        if (keycloak.authenticated) {
            onHandleUpdateToken();
            saveTokenOnLocalStorage(keycloak?.token);

            let roles: string[] =
                keycloak.tokenParsed?.realm_access?.roles || [];
            Object.keys(keycloak.tokenParsed?.resource_access).map((client) => {
                if (["realm-management", "account"].indexOf(client) === -1) {
                    roles = roles.concat(
                        keycloak.tokenParsed.resource_access[client].roles
                    );
                }
            });
            const isAuthenticated: boolean =
                keycloak?.authenticated !== undefined
                    ? keycloak?.authenticated
                    : false;

            if (keycloak.idToken) {
                if (isAuthenticated) {
                    if (!configurationParamsSaved) {
                        window.back_env = {};
                        setConfigurationParamsSaved(true);
                        getUserPreferences().then((res) => {
                            updatePreferences(
                                res.data.language,
                                res.data.dateFormat,
                                res.data.timeFormat,
                                res.data.weekStartDay
                            );

                            updateLanguage(res.data.language);
                        });
                        getConfigurationParams()
                            .then((res) => {
                                if (res.data && res.data.length > 0) {
                                    const configurationParams: ConfigurationParamsType[] =
                                        res.data;
                                    configurationParams.forEach(
                                        (configurationParam) => {
                                            window.back_env[
                                                configurationParam.name
                                            ] = configurationParam.value;
                                        }
                                    );
                                }

                                const titleId =
                                    document.getElementById("titleId");

                                if (titleId) {
                                    titleId.innerHTML =
                                        getEnvVariable(REACT_APP_TITLE_NAME);
                                }
                                onSetLogin(
                                    keycloak?.authenticated !== undefined
                                        ? keycloak?.authenticated
                                        : false,
                                    roles,
                                    keycloak
                                );
                            })
                            .catch(() => {
                                onSetLogin(
                                    keycloak?.authenticated !== undefined
                                        ? keycloak?.authenticated
                                        : false,
                                    roles,
                                    keycloak
                                );
                            });
                    }
                } else {
                    onSetLogin(false, [], keycloak);
                }
            }

            history.push(history.location.pathname);
        }
    }, [keycloak.authenticated, history.location.pathname]);

    return null;
};

const mapDispatchToProps = (dispatch: Dispatch) => {
    return {
        onSetLogin: (
            auth: boolean,
            roles: Array<string>,
            keycloak: Keycloak.KeycloakInstance
        ) => dispatch(setLogIn(auth, roles, keycloak)),
        updateLanguage: (language: string) => {
            dispatch(updateLanguage(language));
        },
        updatePreferences: (
            language: string,
            dateFormat: string,
            timeFormat: string,
            weekStartDay: string
        ) =>
            dispatch(
                setUserPreferences(
                    language,
                    dateFormat,
                    timeFormat,
                    weekStartDay
                )
            ),
    };
};

export default connect(null, mapDispatchToProps)(KeycloakAuth);
